import { Group } from '@mui/icons-material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ClearIcon from '@mui/icons-material/Clear';
import FolderCopyIcon from '@mui/icons-material/FolderCopy';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import SquareIcon from '@mui/icons-material/Square';
import TodayIcon from '@mui/icons-material/Today';
import {
    Alert,
    Button,
    Card,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Theme,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { Stack } from '@mui/system';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import moment, { Moment } from 'moment';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { OnlyFansSubscriber } from '../../hooks/useSubscriber';
import { OnlyFansSubscriberCategory } from '../../hooks/useSubscriberCategories';
import useSubscribers, { SubscribersAdmin } from '../../hooks/useSubscribers';
import { OnlyFansTransactionsDailyStats } from '../../hooks/useTransactionsDailyStats';
import { UserContext } from '../../store/UserContext';
import { formatUsdMoney, useAnimatedSummaryNumber } from '../../utils/animatedNumber';
import { d2f } from '../../utils/common';
import SharedCreditTransferDialog from '../common/SharedCreditTransferDialog';
import DashboardMyAccountsManageCategoriesDialog from './DashboardMyAccountsManageCategoriesDialog';
import DashboardMyAccountsSortMenuButton from './DashboardMyAccountsSortMenuButton';
import DashboardMyAccountsSubscriberCard from './DashboardMyAccountsSubscriberCard';
import DashboardMyAccountsSubscriberCardLarge from './DashboardMyAccountsSubscriberCardLarge';
import DashboardMyAccountsSummaryStatCard from './DashboardMyAccountsSummaryStatCard';

type SubscriberTransactionDailyStats = {
    today: OnlyFansTransactionsDailyStats | null;
    comparison: OnlyFansTransactionsDailyStats | null;
};
type SummaryLoadProgress = {
    earnings: boolean;
    today: boolean;
    comparison: boolean;
    newSubscribers: boolean;
};

// Sort the subscribers based on the sortBy value and the value in the stats array
const sortSubscribers = (
    subscribers: OnlyFansSubscriber[] | SubscribersAdmin | undefined,
    stats: Record<string, any>,
    filter: string,
    sortBy: string,
    sortDirection: 'asc' | 'desc',
) => {
    if (!subscribers || !stats || !Array.isArray(subscribers) || subscribers.length === 0) {
        return [];
    }

    const copyOfSubscribers = [...subscribers];

    if (sortBy === 'isLoggedIn') {
        copyOfSubscribers.sort((a: OnlyFansSubscriber, b: OnlyFansSubscriber) => {
            // compare two booleans isLoggedIn
            return Number(a.isLoggedIn) - Number(b.isLoggedIn);
        });
    } else if (sortBy === 'email') {
        copyOfSubscribers.sort((a: OnlyFansSubscriber, b: OnlyFansSubscriber) => {
            // compare two strings
            return a.email.localeCompare(b.email);
        });
    } else if (sortBy === 'username') {
        copyOfSubscribers.sort((a: OnlyFansSubscriber, b: OnlyFansSubscriber) => {
            // compare two strings
            return a.username.localeCompare(b.username);
        });
    } else if (sortBy === 'credit') {
        copyOfSubscribers.sort((a: OnlyFansSubscriber, b: OnlyFansSubscriber) => {
            return (
                (a.financial && a.financial.sharedCredit ? d2f(a.financial.sharedCredit) : 0) -
                (b.financial && b.financial.sharedCredit ? d2f(b.financial.sharedCredit) : 0)
            );
        });
    } else if (sortBy === 'category') {
        copyOfSubscribers.sort((a: OnlyFansSubscriber, b: OnlyFansSubscriber) => {
            // Get the category name for each subscriber
            const categoryA: string | undefined = a.cognito && 'category' in a.cognito ? a.cognito.category : undefined;
            const categoryB: string | undefined = b.cognito && 'category' in b.cognito ? b.cognito.category : undefined;

            // If the category is not set, then set it to an empty string
            if (!categoryA) {
                return 0;
            }

            if (!categoryB) {
                return 0;
            }

            // compare two strings
            return categoryA.localeCompare(categoryB);
        });
    } else if (sortBy === 'fans') {
        // Use the fans data in the stats array to sort the subscribers
        copyOfSubscribers.sort((a: any, b: any) => {
            // Find the subscriber in the stats array and compare the fans
            const subA = stats[a._id];
            const subB = stats[b._id];

            if (!subA || !subA[0] || !subA[0].counters) {
                return 0;
            }

            if (!subB || !subB[0] || !subB[0].counters) {
                return 0;
            }

            return subA[0].counters.subscribers.active - subB[0].counters.subscribers.active;
        });
    } else if (sortBy === 'following') {
        // Use the following data in the stats array to sort the subscribers
        copyOfSubscribers.sort((a: any, b: any) => {
            // Find the subscriber in the stats array and compare the following
            const subA = stats[a._id];
            const subB = stats[b._id];

            if (!subA || !subA[0] || !subA[0].counters) {
                return 0;
            }

            if (!subB || !subB[0] || !subB[0].counters) {
                return 0;
            }

            return subA[0].counters.subscriptions.active - subB[0].counters.subscriptions.active;
        });
    } else if (sortBy === 'ranking') {
        // Use the ranking data in the stats array to sort the subscribers
        copyOfSubscribers.sort((a: any, b: any) => {
            // Find the subscriber in the stats array and compare the ranking
            const subA = stats[a._id];
            const subB = stats[b._id];

            if (!subA || !subA[0] || !subA[0].performerTop) {
                return 0;
            }

            if (!subB || !subB[0] || !subB[0].performerTop) {
                return 0;
            }

            return subA[0].performerTop - subB[0].performerTop;
        });
    } else if (sortBy === 'earnings') {
        // Use the earnings data in the stats array to sort the subscribers
        copyOfSubscribers.sort((a: any, b: any) => {
            // Find the subscriber in the stats array and compare the earnings
            const subA = stats[a._id];
            const subB = stats[b._id];

            if (!subA || !subA[0] || !subA[0].earnings || !subA[0].earnings.list || !subA[0].earnings.list.total.all.total_gross) {
                return 0;
            }

            if (!subB || !subB[0] || !subB[0].earnings || !subB[0].earnings.list || !subB[0].earnings.list.total.all.total_gross) {
                return 0;
            }

            return subA[0].earnings.list.total.all.total_gross - subB[0].earnings.list.total.all.total_gross;
        });
    }

    const filteredSubscribers = copyOfSubscribers.filter(subscriber => subscriber.username.toLowerCase().includes(filter.toLowerCase()));

    if (sortDirection === 'desc') {
        filteredSubscribers.reverse();
    }

    return filteredSubscribers;
};

const FilterTextFiled = ({ filter, setFilter }: any) => {
    const theme: Theme = useTheme();

    return (
        <FormControl fullWidth>
            <InputLabel id="search-label"></InputLabel>
            <TextField
                id="search"
                label="Search"
                value={filter}
                fullWidth
                size="small"
                onChange={e => setFilter(e.target.value)}
                onKeyDown={e => {
                    if (e.key === 'Escape') {
                        setFilter('');
                    }
                }}
                InputProps={{
                    endAdornment: (
                        <ClearIcon
                            onClick={() => setFilter('')}
                            color="error"
                            sx={{
                                cursor: 'pointer',
                                visibility: filter.length > 0 ? 'visible' : 'hidden',
                            }}
                        />
                    ),
                }}
                sx={{
                    backgroundColor: theme.palette.background.paper,
                    mb: 0,
                    mt: 0,
                }}
            />
        </FormControl>
    );
};

const GroupByCategoryToggle = ({ groupByCategory, setGroupByCategory }: any) => {
    return (
        <Tooltip title="Toggle grouping subscribers by their assigned category" placement="top">
            <span>
                <ToggleButton
                    color="primary"
                    size="small"
                    value="check"
                    selected={groupByCategory}
                    onChange={() => setGroupByCategory(!groupByCategory)}
                >
                    <Group fontSize="small" />
                </ToggleButton>
            </span>
        </Tooltip>
    );
};

const SubscribersTableForLargeScreens = ({
    subscribers,
    setStats,
    refetchSubscribers,
    openTransferCreditDialog,
    amountType,
    todayDate,
    comparisonDate,
    onUpdateDailyTransactionStats,
    onUpdateNewSubscribersToday,
    onUpdateSummaryLoadProgress,
    onOpenComparisonDateDialog,
}: {
    subscribers: OnlyFansSubscriber[];
    setStats: any;
    refetchSubscribers: () => void;
    categoryName?: string;
    openTransferCreditDialog: (subscriber: any) => void;
    amountType: 'gross' | 'net';
    todayDate: string;
    comparisonDate: string;
    onUpdateDailyTransactionStats: (subscriberId: string, stats: SubscriberTransactionDailyStats) => void;
    onUpdateNewSubscribersToday: (subscriberId: string, count: number) => void;
    onUpdateSummaryLoadProgress: (subscriberId: string, progress: SummaryLoadProgress) => void;
    onOpenComparisonDateDialog: () => void;
}) => {
    return (
        <>
            {subscribers.length > 0 ? (
                <Card
                    sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        width: '100%',
                        mb: 1,
                    }}
                >
                    <div style={{ overflow: 'hidden' }}>
                        <TableContainer>
                            <Table sx={{ display: 'table', width: '100%', minWidth: '1100px' }} size="medium">
                                <TableHead>
                                    <TableRow>
                                        <TableCell align="center"></TableCell>
                                        <TableCell align="center">Credit</TableCell>
                                        <TableCell align="center">Fans</TableCell>
                                        <TableCell align="center">Following</TableCell>
                                        <TableCell align="center">Ranking</TableCell>
                                        <TableCell align="center">Earnings</TableCell>
                                        <TableCell align="center">
                                            <Stack spacing={0.25} alignItems="center" justifyContent="center">
                                                <Typography variant="body2">Today's Earnings</Typography>
                                                <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                                                    <Typography variant="body2" color="text.secondary">
                                                        {moment(comparisonDate).format('ddd, MMM D')}
                                                    </Typography>
                                                    <Tooltip title="Select comparison date">
                                                        <IconButton size="small" color="primary" onClick={onOpenComparisonDateDialog}>
                                                            <CalendarMonthIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Stack>
                                            </Stack>
                                        </TableCell>
                                        <TableCell align="center">New Subs.</TableCell>
                                        <TableCell align="center">Services</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {subscribers.map((subscriber: any) => (
                                        <DashboardMyAccountsSubscriberCardLarge
                                            subscriber={subscriber}
                                            setStats={setStats}
                                            refetchSubscribers={refetchSubscribers}
                                            openTransferCreditDialog={openTransferCreditDialog}
                                            amountType={amountType}
                                            todayDate={todayDate}
                                            comparisonDate={comparisonDate}
                                            onUpdateDailyTransactionStats={onUpdateDailyTransactionStats}
                                            onUpdateNewSubscribersToday={onUpdateNewSubscribersToday}
                                            onUpdateSummaryLoadProgress={onUpdateSummaryLoadProgress}
                                            key={subscriber._id}
                                        />
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </div>
                </Card>
            ) : null}
        </>
    );
};

const DashboardMyAccounts = () => {
    const userContext = useContext(UserContext);
    const { data: subscribers, isLoading: subscribersLoading, refetch: refetchSubscribers } = useSubscribers();
    const theme: Theme = useTheme();
    const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
    const [filter, setFilter] = useState('');
    const [sortBy, setSortBy] = useState('username');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
    const [groupByCategory, setGroupByCategory] = useState(true);
    const [stats, setStats] = useState<Record<string, any>>({});
    const [openManageCategoriesDialog, setOpenManageCategoriesDialog] = useState(false);
    const [openComparisonDateDialog, setOpenComparisonDateDialog] = useState(false);
    const [transferCreditDialogOpen, setTransferCreditDialogOpen] = useState<boolean>(false);
    const [transferCreditSourceSubscriber, setTransferCreditSourceSubscriber] = useState<any>(null);
    const [amountType, setAmountType] = useState<'gross' | 'net'>(localStorage.getItem('showEarningsAsGross') === 'true' ? 'gross' : 'net');
    const [comparisonDate, setComparisonDate] = useState<Moment>(moment().subtract(1, 'day').startOf('day'));
    const [pendingComparisonDate, setPendingComparisonDate] = useState<Moment>(moment().subtract(1, 'day').startOf('day'));
    const [dailyTransactionStats, setDailyTransactionStats] = useState<Record<string, SubscriberTransactionDailyStats>>({});
    const [newSubscribersTodayCounts, setNewSubscribersTodayCounts] = useState<Record<string, number>>({});
    const [summaryLoadProgressBySubscriber, setSummaryLoadProgressBySubscriber] = useState<Record<string, SummaryLoadProgress>>({});
    const timezone: string = useMemo(() => moment.tz.guess(), []);
    const todayDate: string = useMemo(() => moment().tz(timezone).format('YYYY-MM-DD'), [timezone]);
    const comparisonDateString: string = useMemo(() => comparisonDate.tz(timezone).format('YYYY-MM-DD'), [comparisonDate, timezone]);

    const [data, setData] = useState<{
        uniqueCategories: OnlyFansSubscriberCategory[];
        groupedSubscribers: {
            [key: string]: {
                category: OnlyFansSubscriberCategory;
                subscribers: OnlyFansSubscriber[];
            };
        };
    }>({
        uniqueCategories: [],
        groupedSubscribers: {},
    });

    useEffect(() => {
        if (!userContext.isAdmin && subscribers && Array.isArray(subscribers)) {
            // Create an empty array to store the stats with the subscriber ID as the key
            const newStats: Record<string, any> = {};

            // Loop through the subscribers and add the stats to the newStats array
            subscribers.forEach((subscriber: any) => {
                if (subscriber._id) {
                    newStats[subscriber._id] = {};
                }
            });

            setStats(newStats);
        }
    }, [subscribers, userContext.isAdmin]);

    useEffect(() => {
        if (!subscribersLoading && subscribers && Array.isArray(subscribers) && subscribers.length > 0) {
            // Extract unique categories
            const uniqueCategories: OnlyFansSubscriberCategory[] = [];
            const categoryMap = new Map();

            subscribers.forEach(subscriber => {
                if (subscriber.cognito && subscriber.cognito.categoryDetails && subscriber.cognito.categoryDetails.length > 0) {
                    subscriber.cognito.categoryDetails.forEach(category => {
                        if (!categoryMap.has(category._id)) {
                            categoryMap.set(category._id, category);
                            uniqueCategories.push(category);
                        }
                    });
                }
            });

            // Group subscribers by category
            const groupedSubscribers: {
                [key: string]: {
                    category: OnlyFansSubscriberCategory;
                    subscribers: OnlyFansSubscriber[];
                };
            } = {};

            subscribers.forEach(subscriber => {
                if (subscriber.cognito && subscriber.cognito.categoryDetails && subscriber.cognito.categoryDetails.length > 0) {
                    const categoryId = subscriber.cognito.categoryDetails[0]._id!;

                    if (!groupedSubscribers[categoryId]) {
                        groupedSubscribers[categoryId] = {
                            category: subscriber.cognito.categoryDetails[0],
                            subscribers: [],
                        };
                    }

                    groupedSubscribers[categoryId].subscribers.push(subscriber);
                }
            });

            setData({ uniqueCategories, groupedSubscribers });
        }
    }, [subscribers, subscribersLoading]);

    const handleManageCategotiesDialogClosed = () => {
        setOpenManageCategoriesDialog(false);
    };

    const openTransferCreditDialog = useCallback((sourceSubscriber: any) => {
        setTransferCreditSourceSubscriber(sourceSubscriber);
        setTransferCreditDialogOpen(true);
    }, []);

    const setAmountTypeAndPersist = useCallback((newValue: 'gross' | 'net') => {
        localStorage.setItem('showEarningsAsGross', newValue === 'gross' ? 'true' : 'false');
        setAmountType(newValue);
    }, []);

    const onUpdateDailyTransactionStats = useCallback((subscriberId: string, subscriberStats: SubscriberTransactionDailyStats) => {
        setDailyTransactionStats(prev => ({
            ...prev,
            [String(subscriberId)]: subscriberStats,
        }));
    }, []);

    const onUpdateNewSubscribersToday = useCallback((subscriberId: string, count: number) => {
        const safeCount = Number.isFinite(Number(count)) ? Number(count) : 0;
        setNewSubscribersTodayCounts(prev => ({
            ...prev,
            [String(subscriberId)]: safeCount,
        }));
    }, []);

    const onUpdateSummaryLoadProgress = useCallback((subscriberId: string, progress: SummaryLoadProgress) => {
        setSummaryLoadProgressBySubscriber(prev => ({
            ...prev,
            [String(subscriberId)]: progress,
        }));
    }, []);

    const subscribersWithoutCategory = useMemo(
        () =>
            subscribers && Array.isArray(subscribers)
                ? subscribers.filter(
                      (subscriber: OnlyFansSubscriber) =>
                          !subscriber.cognito || !subscriber.cognito.category || !subscriber.cognito.categoryDetails,
                  )
                : [],
        [subscribers],
    );

    const sortedUncategorized = useMemo(
        () => sortSubscribers(subscribersWithoutCategory, stats, filter, sortBy, sortDirection),
        [subscribersWithoutCategory, stats, filter, sortBy, sortDirection],
    );

    const sortedAll = useMemo(
        () => (subscribers && Array.isArray(subscribers) ? sortSubscribers(subscribers, stats, filter, sortBy, sortDirection) : []),
        [subscribers, stats, filter, sortBy, sortDirection],
    );

    const sortedByCategoryMap = useMemo(() => {
        const map: Record<string, OnlyFansSubscriber[]> = {};
        data.uniqueCategories.forEach((category: OnlyFansSubscriberCategory) => {
            const subs = data.groupedSubscribers[category._id!]?.subscribers ?? [];
            map[category._id!] = sortSubscribers(subs, stats, filter, sortBy, sortDirection);
        });
        return map;
    }, [data.uniqueCategories, data.groupedSubscribers, stats, filter, sortBy, sortDirection]);

    const visibleSubscribers = useMemo(() => {
        if (groupByCategory) {
            const grouped = data.uniqueCategories.flatMap(
                (category: OnlyFansSubscriberCategory) => sortedByCategoryMap[category._id!] ?? [],
            );

            return [...sortedUncategorized, ...grouped];
        }

        return sortedAll;
    }, [groupByCategory, data.uniqueCategories, sortedByCategoryMap, sortedUncategorized, sortedAll]);

    const earningsFromMetrics = useCallback(
        (subscriberId: string): number => {
            const subscriberStats = stats[subscriberId];
            if (!subscriberStats || !subscriberStats[0]?.earnings?.list?.total?.all) {
                return 0;
            }

            return amountType === 'gross'
                ? subscriberStats[0].earnings.list.total.all.total_gross || 0
                : subscriberStats[0].earnings.list.total.all.total_net || 0;
        },
        [stats, amountType],
    );

    const summaryTotals = useMemo(() => {
        return visibleSubscribers.reduce(
            (acc, subscriber: OnlyFansSubscriber) => {
                if (!subscriber._id) {
                    return acc;
                }

                const subscriberStats = stats[subscriber._id];
                const subscriberDaily = dailyTransactionStats[String(subscriber._id)];
                const todayRaw = amountType === 'gross' ? subscriberDaily?.today?.totalAmount ?? 0 : subscriberDaily?.today?.totalNet ?? 0;
                const comparisonRaw =
                    amountType === 'gross' ? subscriberDaily?.comparison?.totalAmount ?? 0 : subscriberDaily?.comparison?.totalNet ?? 0;
                const todayValue = Number(todayRaw);
                const comparisonValue = Number(comparisonRaw);
                const todayNum = Number.isFinite(todayValue) ? todayValue : 0;
                const comparisonNum = Number.isFinite(comparisonValue) ? comparisonValue : 0;
                const newSubscribersToday = Number(newSubscribersTodayCounts[String(subscriber._id)] ?? 0);

                return {
                    earnings: acc.earnings + earningsFromMetrics(subscriber._id),
                    fans: acc.fans + (subscriberStats?.[0]?.counters?.subscribers?.active || 0),
                    following: acc.following + (subscriberStats?.[0]?.counters?.subscriptions?.active || 0),
                    today: acc.today + todayNum,
                    comparison: acc.comparison + comparisonNum,
                    newSubscribersToday: acc.newSubscribersToday + (Number.isFinite(newSubscribersToday) ? newSubscribersToday : 0),
                };
            },
            {
                earnings: 0,
                fans: 0,
                following: 0,
                today: 0,
                comparison: 0,
                newSubscribersToday: 0,
            },
        );
    }, [visibleSubscribers, stats, dailyTransactionStats, amountType, earningsFromMetrics, newSubscribersTodayCounts]);

    const totalEarningsDisplay = useAnimatedSummaryNumber(summaryTotals.earnings);
    const totalTodayDisplay = useAnimatedSummaryNumber(summaryTotals.today);
    const totalComparisonDisplay = useAnimatedSummaryNumber(summaryTotals.comparison);
    const totalNewSubscribersTodayDisplay = useAnimatedSummaryNumber(summaryTotals.newSubscribersToday);
    const visibleSubscriberIds = useMemo(
        () => visibleSubscribers.map(subscriber => String(subscriber._id)).filter(id => id && id !== 'undefined'),
        [visibleSubscribers],
    );
    const summaryProgress = useMemo(() => {
        const total = visibleSubscriberIds.length;
        const counts = {
            earnings: 0,
            today: 0,
            comparison: 0,
            newSubscribers: 0,
        };

        visibleSubscriberIds.forEach(subscriberId => {
            const progress = summaryLoadProgressBySubscriber[subscriberId];
            if (!progress) {
                return;
            }

            if (progress.earnings) {
                counts.earnings += 1;
            }
            if (progress.today) {
                counts.today += 1;
            }
            if (progress.comparison) {
                counts.comparison += 1;
            }
            if (progress.newSubscribers) {
                counts.newSubscribers += 1;
            }
        });

        return { total, ...counts };
    }, [visibleSubscriberIds, summaryLoadProgressBySubscriber]);

    return (
        <>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="h6">My Accounts</Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Button
                        fullWidth
                        component={Link}
                        to="/dashboard/analytics"
                        variant="contained"
                        startIcon={<AssessmentIcon />}
                        size="small"
                        sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
                    >
                        Open Report Card
                    </Button>
                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        color="primary"
                        value={amountType}
                        onChange={(_, value: 'gross' | 'net' | null) => {
                            if (value) {
                                setAmountTypeAndPersist(value);
                            }
                        }}
                    >
                        <ToggleButton sx={{ px: 1.25, py: 0.25, fontSize: '0.7rem' }} value="net">
                            Net
                        </ToggleButton>
                        <ToggleButton sx={{ px: 1.25, py: 0.25, fontSize: '0.7rem' }} value="gross">
                            Gross
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Stack>
            </Stack>

            <Grid container spacing={1} sx={{ mb: 1 }}>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                    <DashboardMyAccountsSummaryStatCard
                        icon={<AttachMoneyIcon sx={{ fontSize: 28, color: theme.palette.primary.main }} />}
                        title="Total Earnings"
                        subtitle={amountType}
                        value={formatUsdMoney(totalEarningsDisplay)}
                        progressCurrent={summaryProgress.earnings}
                        progressMax={summaryProgress.total}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                    <DashboardMyAccountsSummaryStatCard
                        icon={<TodayIcon sx={{ fontSize: 28, color: theme.palette.primary.main }} />}
                        title="Total Today"
                        subtitle={moment().format('ddd, MMM D')}
                        value={formatUsdMoney(totalTodayDisplay)}
                        progressCurrent={summaryProgress.today}
                        progressMax={summaryProgress.total}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                    <DashboardMyAccountsSummaryStatCard
                        icon={<CalendarMonthIcon sx={{ fontSize: 28, color: theme.palette.secondary.main }} />}
                        title="Selected Day"
                        subtitle={comparisonDate.format('ddd, MMM D')}
                        value={formatUsdMoney(totalComparisonDisplay)}
                        progressCurrent={summaryProgress.comparison}
                        progressMax={summaryProgress.total}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                    <DashboardMyAccountsSummaryStatCard
                        icon={<PersonAddAlt1Icon sx={{ fontSize: 28, color: theme.palette.success.main }} />}
                        title="New Subscribers"
                        subtitle="today (GMT)"
                        value={Math.round(totalNewSubscribersTodayDisplay).toLocaleString()}
                        progressCurrent={summaryProgress.newSubscribers}
                        progressMax={summaryProgress.total}
                    />
                </Grid>
                <Grid item xs={12} sx={{ display: { xs: 'block', sm: 'none' } }}>
                    <Button
                        fullWidth
                        component={Link}
                        to="/dashboard/analytics"
                        variant="contained"
                        startIcon={<AssessmentIcon />}
                        size="small"
                    >
                        Open Report Card
                    </Button>
                </Grid>
            </Grid>

            <Grid container spacing={0.5} alignItems="center" sx={{ mb: 1 }}>
                <Grid item xs>
                    <FilterTextFiled filter={filter} setFilter={setFilter} />
                </Grid>
                <Grid item xs="auto">
                    <DashboardMyAccountsSortMenuButton
                        sortBy={sortBy}
                        setSortBy={setSortBy}
                        sortDirection={sortDirection}
                        setSortDirection={setSortDirection}
                    />
                </Grid>
                <Grid item xs="auto">
                    <GroupByCategoryToggle groupByCategory={groupByCategory} setGroupByCategory={setGroupByCategory} />
                </Grid>
                <Grid item xs="auto">
                    <Tooltip title="Manage Categories">
                        <IconButton
                            color="primary"
                            size="small"
                            onClick={() => {
                                setOpenManageCategoriesDialog(true);
                            }}
                        >
                            <FolderCopyIcon fontSize="medium" />
                        </IconButton>
                    </Tooltip>
                </Grid>
            </Grid>

            {subscribersLoading ? (
                <Skeleton />
            ) : (
                <>
                    {subscribers && Array.isArray(subscribers) && subscribers.length > 0 ? (
                        <>
                            {groupByCategory ? (
                                <>
                                    {isLargeScreen ? (
                                        <SubscribersTableForLargeScreens
                                            subscribers={sortedUncategorized}
                                            setStats={setStats}
                                            refetchSubscribers={refetchSubscribers}
                                            openTransferCreditDialog={openTransferCreditDialog}
                                            amountType={amountType}
                                            todayDate={todayDate}
                                            comparisonDate={comparisonDateString}
                                            onUpdateDailyTransactionStats={onUpdateDailyTransactionStats}
                                            onUpdateNewSubscribersToday={onUpdateNewSubscribersToday}
                                            onUpdateSummaryLoadProgress={onUpdateSummaryLoadProgress}
                                            onOpenComparisonDateDialog={() => {
                                                setPendingComparisonDate(comparisonDate);
                                                setOpenComparisonDateDialog(true);
                                            }}
                                        />
                                    ) : (
                                        <Grid container>
                                            {sortedUncategorized.map((subscriber: OnlyFansSubscriber) => (
                                                <Grid item xs={12} sm={6} md={4} sx={{ mb: 1 }} key={subscriber._id}>
                                                    <DashboardMyAccountsSubscriberCard
                                                        subscriber={subscriber}
                                                        setStats={setStats}
                                                        refetchSubscribers={refetchSubscribers}
                                                        openTransferCreditDialog={openTransferCreditDialog}
                                                        amountType={amountType}
                                                        todayDate={todayDate}
                                                        comparisonDate={comparisonDateString}
                                                        onOpenComparisonDateDialog={() => {
                                                            setPendingComparisonDate(comparisonDate);
                                                            setOpenComparisonDateDialog(true);
                                                        }}
                                                        onUpdateDailyTransactionStats={onUpdateDailyTransactionStats}
                                                        onUpdateNewSubscribersToday={onUpdateNewSubscribersToday}
                                                        onUpdateSummaryLoadProgress={onUpdateSummaryLoadProgress}
                                                    />
                                                </Grid>
                                            ))}
                                        </Grid>
                                    )}

                                    {data.uniqueCategories.map((category: OnlyFansSubscriberCategory) => (
                                        <React.Fragment key={category._id}>
                                            <Typography variant="h6" sx={{ mb: 2, mt: 2 }}>
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <SquareIcon
                                                        sx={{
                                                            ...(!category.color || category.color === 'none'
                                                                ? {
                                                                      color: theme.palette.background.paper,
                                                                      border: `1px solid ${theme.palette.divider}`,
                                                                  }
                                                                : {
                                                                      color: category.color,
                                                                  }),
                                                            mr: 1,
                                                        }}
                                                    />
                                                    {category.name}
                                                </Stack>
                                            </Typography>

                                            {isLargeScreen ? (
                                                <SubscribersTableForLargeScreens
                                                    subscribers={sortedByCategoryMap[category._id!] ?? []}
                                                    setStats={setStats}
                                                    refetchSubscribers={refetchSubscribers}
                                                    openTransferCreditDialog={openTransferCreditDialog}
                                                    amountType={amountType}
                                                    todayDate={todayDate}
                                                    comparisonDate={comparisonDateString}
                                                    onUpdateDailyTransactionStats={onUpdateDailyTransactionStats}
                                                    onUpdateNewSubscribersToday={onUpdateNewSubscribersToday}
                                                    onUpdateSummaryLoadProgress={onUpdateSummaryLoadProgress}
                                                    onOpenComparisonDateDialog={() => {
                                                        setPendingComparisonDate(comparisonDate);
                                                        setOpenComparisonDateDialog(true);
                                                    }}
                                                />
                                            ) : (
                                                <Grid container>
                                                    {(sortedByCategoryMap[category._id!] ?? []).map((subscriber: OnlyFansSubscriber) => (
                                                        <Grid item xs={12} sm={6} md={4} sx={{ mb: 1 }} key={subscriber._id}>
                                                            <DashboardMyAccountsSubscriberCard
                                                                subscriber={subscriber}
                                                                setStats={setStats}
                                                                refetchSubscribers={refetchSubscribers}
                                                                openTransferCreditDialog={openTransferCreditDialog}
                                                                amountType={amountType}
                                                                todayDate={todayDate}
                                                                comparisonDate={comparisonDateString}
                                                                onOpenComparisonDateDialog={() => {
                                                                    setPendingComparisonDate(comparisonDate);
                                                                    setOpenComparisonDateDialog(true);
                                                                }}
                                                                onUpdateDailyTransactionStats={onUpdateDailyTransactionStats}
                                                                onUpdateNewSubscribersToday={onUpdateNewSubscribersToday}
                                                                onUpdateSummaryLoadProgress={onUpdateSummaryLoadProgress}
                                                            />
                                                        </Grid>
                                                    ))}
                                                </Grid>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </>
                            ) : (
                                <>
                                    {isLargeScreen ? (
                                        <SubscribersTableForLargeScreens
                                            subscribers={sortedAll}
                                            setStats={setStats}
                                            refetchSubscribers={refetchSubscribers}
                                            openTransferCreditDialog={openTransferCreditDialog}
                                            amountType={amountType}
                                            todayDate={todayDate}
                                            comparisonDate={comparisonDateString}
                                            onUpdateDailyTransactionStats={onUpdateDailyTransactionStats}
                                            onUpdateNewSubscribersToday={onUpdateNewSubscribersToday}
                                            onUpdateSummaryLoadProgress={onUpdateSummaryLoadProgress}
                                            onOpenComparisonDateDialog={() => {
                                                setPendingComparisonDate(comparisonDate);
                                                setOpenComparisonDateDialog(true);
                                            }}
                                        />
                                    ) : (
                                        <Grid container>
                                            {sortedAll.map((subscriber: OnlyFansSubscriber) => (
                                                <Grid item xs={12} sm={6} md={4} sx={{ mb: 1 }} key={subscriber._id}>
                                                    <DashboardMyAccountsSubscriberCard
                                                        subscriber={subscriber}
                                                        setStats={setStats}
                                                        refetchSubscribers={refetchSubscribers}
                                                        openTransferCreditDialog={openTransferCreditDialog}
                                                        amountType={amountType}
                                                        todayDate={todayDate}
                                                        comparisonDate={comparisonDateString}
                                                        onOpenComparisonDateDialog={() => {
                                                            setPendingComparisonDate(comparisonDate);
                                                            setOpenComparisonDateDialog(true);
                                                        }}
                                                        onUpdateDailyTransactionStats={onUpdateDailyTransactionStats}
                                                        onUpdateNewSubscribersToday={onUpdateNewSubscribersToday}
                                                        onUpdateSummaryLoadProgress={onUpdateSummaryLoadProgress}
                                                    />
                                                </Grid>
                                            ))}
                                        </Grid>
                                    )}
                                </>
                            )}

                            <DashboardMyAccountsManageCategoriesDialog
                                open={openManageCategoriesDialog}
                                onClose={handleManageCategotiesDialogClosed}
                            />
                        </>
                    ) : (
                        <Alert severity="warning">No accounts are currently setup.</Alert>
                    )}
                </>
            )}

            {transferCreditSourceSubscriber && (
                <SharedCreditTransferDialog
                    open={transferCreditDialogOpen}
                    onClose={() => {
                        setTransferCreditDialogOpen(false);
                    }}
                    sourceSubscriber={transferCreditSourceSubscriber}
                />
            )}

            <Dialog open={openComparisonDateDialog} onClose={() => setOpenComparisonDateDialog(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Select comparison date</DialogTitle>
                <DialogContent>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                        <DatePicker
                            label="Comparison date"
                            value={pendingComparisonDate}
                            onChange={(value: any) => {
                                if (value && moment.isMoment(value)) {
                                    setPendingComparisonDate(value.clone().startOf('day'));
                                }
                            }}
                            disableFuture
                            renderInput={params => <TextField {...params} fullWidth margin="normal" />}
                        />
                    </LocalizationProvider>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenComparisonDateDialog(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            setComparisonDate(pendingComparisonDate.startOf('day'));
                            setOpenComparisonDateDialog(false);
                        }}
                    >
                        Apply
                    </Button>
                </DialogActions>
            </Dialog>

            <Grid container spacing={1} sx={{ marginBottom: theme.spacing(2) }}>
                <Grid item xs={12}>
                    <Alert severity="info">
                        If you don't see your account (or one of your accounts) on this list, please contact Admin.
                    </Alert>
                </Grid>
            </Grid>
        </>
    );
};

export default DashboardMyAccounts;
